<template>
  <div
    class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen bg-background"
  >
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <!-- Left Sidebar -->
      <div
        class="hidden lg:block lg:col-span-1 space-y-8 h-[calc(100vh-80px)] sticky top-20"
      >
        <div
          class="bg-card rounded-[20px] p-6 shadow-sm border border-border flex flex-col h-full"
        >
          <!-- Following Section -->
          <div class="mb-10 flex-shrink-0">
            <h3 class="font-bold text-[16px] text-foreground mb-4">
              Following
            </h3>
            <div class="space-y-4">
              <div
                v-for="person in followingList"
                :key="person.id"
                class="flex items-center justify-between"
              >
                <div class="flex items-center space-x-3">
                  <img
                    :src="person.avatar"
                    alt="Avatar"
                    class="w-[42px] h-[42px] rounded-full object-cover"
                  />
                  <div>
                    <h4
                      class="font-bold text-[14px] leading-tight text-foreground"
                    >
                      {{ person.name }}
                    </h4>
                    <p class="text-[12px] text-muted-foreground mt-0.5">
                      {{ person.title }}
                    </p>
                  </div>
                </div>
                <button
                  class="px-4 py-1.5 border border-border/80 text-foreground text-[12px] font-medium rounded-full hover:bg-muted/30 transition-colors"
                >
                  Unfollow
                </button>
              </div>
            </div>
          </div>

          <!-- People to Follow Section -->
          <div class="flex-shrink-0">
            <h3 class="font-bold text-[16px] text-foreground mb-4">
              People To Follow
            </h3>
            <div class="space-y-4">
              <div
                v-for="person in suggestedUsers"
                :key="person.id"
                class="flex items-center justify-between"
              >
                <div class="flex items-center space-x-3">
                  <img
                    :src="person.avatar"
                    alt="Avatar"
                    class="w-[42px] h-[42px] rounded-full object-cover"
                  />
                  <div>
                    <h4
                      class="font-bold text-[14px] leading-tight text-foreground"
                    >
                      {{ person.name }}
                    </h4>
                    <p class="text-[12px] text-muted-foreground mt-0.5">
                      {{ person.title }}
                    </p>
                  </div>
                </div>
                <button
                  class="px-4 py-1.5 bg-primary text-primary-foreground text-[12px] font-medium rounded-full hover:bg-primary/90 transition-colors"
                >
                  Follow
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="col-span-1 lg:col-span-3">
        <!-- Header Controls -->
        <div class="flex items-center justify-between mb-12">
          <button
            @click="$router.back()"
            class="flex items-center text-muted-foreground hover:text-foreground font-medium text-[15px] group"
          >
            <div
              class="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mr-3 group-hover:scale-105 transition-transform"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </div>
            Back
          </button>

          <div class="flex justify-center flex-1">
            <div class="relative">
              <span class="text-[17px] font-semibold text-foreground pb-2 px-1"
                >Jobs</span
              >
              <div
                class="absolute bottom-0 left-0 w-full h-0.5 bg-foreground rounded-full"
              ></div>
            </div>
          </div>

          <div class="w-24"></div>
          <!-- Empty div for flex balance -->
        </div>

        <!-- Hero Text -->
        <div class="text-center mb-10">
          <h1
            class="text-[42px] font-medium text-foreground tracking-tight mb-3"
          >
            Find jobs for every level<span class="ml-2">👍🏼</span>
          </h1>
          <p class="text-[16px] text-muted-foreground">
            Your trusted & reliable
            <span class="font-medium text-foreground">job</span>
            board for creatives & techies
          </p>
        </div>

        <!-- Filters -->
        <div class="flex flex-wrap items-center gap-4 mb-8">
          <!-- Search Input -->
          <div class="relative flex-1 min-w-[200px]">
            <input
              type="text"
              v-model="filters.q"
              @input="debounceSearch"
              placeholder="Role or company..."
              class="w-full px-6 py-2.5 bg-card border border-border/80 rounded-full text-[15px] focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all pl-10"
            />
            <svg
              class="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>

          <!-- Location Input -->
          <div class="relative flex-1 min-w-[150px]">
            <input
              type="text"
              v-model="filters.location"
              @input="debounceSearch"
              placeholder="Location..."
              class="w-full px-6 py-2.5 bg-card border border-border/80 rounded-full text-[15px] focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all pl-10"
            />
            <svg
              class="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>
          </div>

          <!-- Job Type -->
          <select
            v-model="filters.job_type"
            @change="handleFilterChange"
            class="px-6 py-2.5 bg-card border border-border/80 rounded-full text-[15px] font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer"
          >
            <option value="">Job type</option>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="freelance">Freelance</option>
            <option value="internship">Internship</option>
            <option value="remote">Remote</option>
            <option value="hybrid">Hybrid</option>
          </select>

          <!-- Experience Level -->
          <select
            v-model="filters.experience_level"
            @change="handleFilterChange"
            class="px-6 py-2.5 bg-card border border-border/80 rounded-full text-[15px] font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer"
          >
            <option value="">Experience level</option>
            <option value="entry_level">Entry Level</option>
            <option value="junior">Junior</option>
            <option value="mid_level">Mid-level</option>
            <option value="senior">Senior</option>
          </select>

          <!-- Clear Filters -->
          <button
            v-if="Object.values(filters).some((f) => f !== '')"
            @click="clearFilters"
            class="px-6 py-2.5 text-primary hover:text-primary/80 font-medium text-[15px] transition-colors"
          >
            Clear filters
          </button>
        </div>

        <!-- Job Listings -->
        <div class="space-y-4">
          <div v-if="loading" class="space-y-4">
            <div
              v-for="n in 6"
              :key="n"
              class="bg-card border border-border rounded-2xl p-5 flex items-start justify-between h-[100px]"
            >
              <div class="flex items-start space-x-4 w-full">
                <SkeletonLoader width="56px" height="56px" class="rounded-xl" />
                <div class="space-y-2 flex-1">
                  <SkeletonLoader width="100px" height="14px" />
                  <SkeletonLoader width="200px" height="20px" />
                </div>
              </div>
            </div>
          </div>
          <template v-else>
            <div
              v-for="job in jobs"
              :key="job.id"
              @click="$router.push(`/jobs/${job.id}`)"
              class="bg-card border border-border rounded-2xl p-5 flex items-start justify-between hover:shadow-sm transition-shadow cursor-pointer group"
            >
              <div class="flex items-start space-x-4">
                <!-- Company Box / Logo Placeholder -->
                <div
                  class="w-14 h-14 bg-muted rounded-xl flex items-center justify-center flex-shrink-0"
                >
                  <svg
                    class="w-6 h-6 text-white/80"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                    />
                  </svg>
                </div>

                <!-- Job Info -->
                <div class="pt-0.5">
                  <p class="text-[13px] text-muted-foreground font-medium mb-1">
                    {{ job.company }}
                  </p>
                  <h3
                    class="text-[18px] font-bold text-foreground group-hover:text-primary transition-colors"
                  >
                    {{ job.title }}
                  </h3>
                </div>
              </div>

              <!-- Meta Info -->
              <div class="text-right pt-0.5">
                <p class="text-[13px] text-muted-foreground mb-2">
                  Posted {{ getPostedTime(job.created_at) }}
                </p>
                <div
                  class="flex items-center justify-end text-[12px] font-bold text-foreground"
                >
                  <svg
                    class="w-3.5 h-3.5 mr-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {{ job.location.toUpperCase() }}
                </div>
              </div>
            </div>
            <div
              v-if="jobs.length === 0"
              class="text-center py-20 bg-card border border-border rounded-2xl"
            >
              <p class="text-muted-foreground">No job postings found.</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import SkeletonLoader from "@/components/SkeletonLoader.vue";

export default {
  name: "JobsView",
  components: {
    SkeletonLoader,
  },
  data() {
    return {
      loading: true,
      jobs: [],
      followingList: [
        {
          id: 1,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 2,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 3,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 4,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
      suggestedUsers: [
        {
          id: 5,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 6,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 7,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 8,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
      // jobListings for design reference - will be replaced by API jobs
      filters: {
        q: "",
        location: "",
        job_type: "",
        experience_level: "",
      },
      searchTimeout: null,
    };
  },
  methods: {
    async fetchJobs() {
      this.loading = true;
      try {
        const params = {};
        if (this.filters.q) params.q = this.filters.q;
        if (this.filters.location) params.location = this.filters.location;
        if (this.filters.job_type) params.job_type = this.filters.job_type;
        if (this.filters.experience_level)
          params.experience_level = this.filters.experience_level;

        const response = await apiClient.get("/web/jobs/", { params });
        if (response.data.status_code === 200) {
          this.jobs = response.data.data.jobs;
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        this.loading = false;
      }
    },
    handleFilterChange() {
      this.fetchJobs();
    },
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchJobs();
      }, 500);
    },
    clearFilters() {
      this.filters = {
        q: "",
        location: "",
        job_type: "",
        experience_level: "",
      };
      this.fetchJobs();
    },
    getPostedTime(dateString) {
      if (!dateString) return "Recently";
      const now = new Date();
      const posted = new Date(dateString);
      const diffInSeconds = Math.floor((now - posted) / 1000);

      if (diffInSeconds < 60) return "Just now";

      const diffInMinutes = Math.floor(diffInSeconds / 60);
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 30) return `${diffInDays}d ago`;

      return posted.toLocaleDateString();
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.fetchJobs();
  },
};
</script>

<style scoped>
/* Scoped overrides if necessary */
</style>
